// Сэтгэл ханамжийн асуулга бөглөх цонх — мобайлд доороос гарах sheet, компьютерт голд.
// Нэг дэлгэцэнд нэг асуулт: од (1–5) эсвэл «Мэдэхгүй» — сонгосон даруйд хадгална; тайлбарыг Дараах/Өмнөх дарахад хадгална.
// Нээхэд хариулаагүй эхний асуултаас. Олон хүүхэдтэй бол хүүхэд бүрээр: эхнийхийг илгээгээд дараагийнх руу (илгээснийг алгасна).
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Spin } from "antd";
import { FiCheck, FiChevronLeft } from "react-icons/fi";
import { instance } from "utils/axios";

const STAR = "M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3 6.1 20.6l1.3-6.6L2.5 9.4l6.6-.8z";
const isAnswered = (a) => !!a && (a.dontKnow || !!a.stars);
// "Бат · 11-р анги"
export const childLabel = (c) => (c ? [c.firstName, c.grade ? `${c.grade}-р анги` : null].filter(Boolean).join(" · ") : "");
const flatQuestions = (d) => (d?.sections || []).flatMap((sec) => sec.questions.map((q) => ({ ...q, section: sec.title })));
const firstOpen = (qs, ans) => {
  const i = qs.findIndex((q) => !isAnswered(ans?.[q.id]));
  return i < 0 ? Math.max(0, qs.length - 1) : i;
};

export default function SurveyFlow({ id, onClose }) {
  const [s, setS] = useState(undefined);
  const [answers, setAnswers] = useState({}); // { [studentId]: { [questionId]: { stars, dontKnow, comment } } }
  const [sid, setSid] = useState(null); // одоо бөглөж буй хүүхэд (сурагч бол өөрөө)
  const [qi, setQi] = useState(0);
  const [comment, setComment] = useState("");
  const [phase, setPhase] = useState("q"); // q | next (нэг хүүхэд илгээгдэж, дараагийнх үлдсэн) | done
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);
  const qRef = useRef(null);

  const questions = useMemo(() => flatQuestions(s), [s]);
  const items = s?.items || [];
  const item = items.find((i) => i.studentId === sid);
  const left = items.filter((i) => !i.submitted);
  const q = questions[qi];
  const a = (q && answers[sid]?.[q.id]) || null;
  const total = questions.length;
  const last = qi === total - 1;

  const begin = (it, d, ans) => {
    const qs = flatQuestions(d);
    const i = firstOpen(qs, ans[it.studentId]);
    setSid(it.studentId);
    setQi(i);
    setComment(ans[it.studentId]?.[qs[i]?.id]?.comment || "");
    setPhase("q");
  };

  useEffect(() => {
    instance({ method: "get", url: `/surveys/${id}` })
      .then((res) => {
        const d = res.status === 204 ? null : res.data;
        setS(d);
        if (!d) return;
        const ans = Object.fromEntries(d.items.map((i) => [i.studentId, i.answers || {}]));
        setAnswers(ans);
        const first = d.items.find((i) => !i.submitted);
        if (first) begin(first, d, ans);
        else setPhase("done");
      })
      .catch(() => setS(null));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Нээгдэхэд арын хуудсыг гүйлгэхгүй, цонх руу фокус
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    boxRef.current?.focus();
    return () => { document.body.style.overflow = prev; };
  }, []);
  // Асуулт солигдоход фокусыг асуултын текст рүү (дэлгэц уншигч шинэ асуултыг уншина)
  useEffect(() => { if (phase === "q" && sid) qRef.current?.focus(); }, [qi, sid, phase]);

  // Нэг асуултын хариултыг серверт — од/мэдэхгүй-г тайлбартай нь хамт (тайлбар илгээхгүй бол сервер арилгана)
  const save = (qq, ans, cmt) =>
    instance({
      method: "put", url: `/surveys/${id}/answer`, silent: true,
      data: { studentId: sid, questionId: qq.id, ...(ans.dontKnow ? { dontKnow: true } : { stars: ans.stars }), comment: cmt },
    }).catch(() => {});

  const setLocal = (qq, ans) => setAnswers((m) => ({ ...m, [sid]: { ...(m[sid] || {}), [qq.id]: ans } }));

  const pickStars = (n) => {
    const ans = { stars: n, dontKnow: false, comment };
    setLocal(q, ans);
    save(q, ans, comment);
  };
  // «Мэдэхгүй» дахин дарвал болиулна — серверт устгах хүсэлт байхгүй тул од сонгох хүртэл хариулаагүй гэж үзнэ
  const toggleDk = () => {
    if (a?.dontKnow) return setLocal(q, { stars: null, dontKnow: false, comment });
    const ans = { stars: null, dontKnow: true, comment };
    setLocal(q, ans);
    save(q, ans, comment);
  };

  // Тайлбар өөрчлөгдсөн бол хадгална (асуултаас гарах үед)
  const flushComment = () => {
    if (!q || !isAnswered(a) || (a.comment || "") === comment) return Promise.resolve();
    const ans = { ...a, comment };
    setLocal(q, ans);
    return save(q, ans, comment);
  };

  const go = (i) => {
    flushComment();
    setQi(i);
    setComment(answers[sid]?.[questions[i].id]?.comment || "");
  };

  const submit = async () => {
    setBusy(true);
    await flushComment();
    try {
      await instance({ method: "post", url: `/surveys/${id}/submit`, silent: true, data: { studentId: sid } });
      const rest = items.filter((i) => !i.submitted && i.studentId !== sid);
      setS((d) => ({ ...d, items: d.items.map((i) => (i.studentId === sid ? { ...i, submitted: true } : i)) }));
      setPhase(rest.length ? "next" : "done");
    } catch (e) {
      // Алдааны текстийг axios interceptor харуулна ("N асуултад хариулаагүй байна")
    }
    setBusy(false);
  };

  const next = () => {
    if (!isAnswered(a) || busy) return;
    if (last) submit();
    else go(qi + 1);
  };

  // «Дараа бөглөх» — өнөөдөр дахин автоматаар гарахгүй
  const later = () => {
    flushComment();
    instance({ method: "post", url: `/surveys/${id}/snooze`, data: {}, silent: true }).catch(() => {});
    onClose();
  };
  const onKey = (e) => {
    if (e.key !== "Escape") return;
    if (phase === "q" && s) later();
    else onClose();
  };

  const nextChild = items.find((i) => !i.submitted);
  const doneChild = phase === "next" ? item : null;

  return (
    <div className="fixed inset-0 z-[1000] bg-black/45 flex items-end md:items-center justify-center" onKeyDown={onKey}>
      <div ref={boxRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={s?.title || "Сэтгэл ханамжийн асуулга"}
        className="w-full md:max-w-[520px] max-h-[100dvh] overflow-y-auto bg-white rounded-t-[22px] md:rounded-[22px] px-5 md:px-7 pt-3 md:pt-6 flex flex-col gap-[18px] min-h-[min(600px,92dvh)] md:min-h-[560px] outline-none"
        style={{ paddingBottom: "calc(24px + var(--safe-bottom))" }}>
        <span aria-hidden="true" className="md:hidden self-center w-10 h-1 rounded-full bg-[#d9cdea]" />

        {s === undefined ? (
          <div className="flex-1 flex items-center justify-center"><Spin /></div>
        ) : !s ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center">
            <span className="text-sm text-[#6e5e8a]">Энэ асуулга олдсонгүй эсвэл хаагдсан байна.</span>
            <button onClick={onClose} className="h-12 px-8 rounded-full bg-primary text-white text-[15px] font-bold hover:bg-[#4b0d76]">Хаах</button>
          </div>
        ) : phase === "q" && q ? (
          <>
            <div className="flex items-center justify-between -mb-2">
              <span className="text-[13px] font-semibold text-[#6e5e8a]">Асуулт {qi + 1} / {total}</span>
              <button onClick={later} className="min-h-[44px] px-1 text-sm font-semibold text-primary hover:text-[#3d1b6e]">Дараа бөглөх</button>
            </div>
            <div className="h-1.5 rounded-full bg-[#eef0f4] overflow-hidden" role="progressbar" aria-label="Явц"
              aria-valuemin={0} aria-valuemax={total} aria-valuenow={qi + (isAnswered(a) ? 1 : 0)}>
              <div className="h-full rounded-full bg-primary transition-[width] duration-200" style={{ width: `${Math.round(((qi + (isAnswered(a) ? 1 : 0)) * 100) / total)}%` }} />
            </div>
            {item?.child && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center h-7 px-3 rounded-full bg-[#f3eeff] border border-[#e5d8f5] text-primary text-xs font-semibold">
                  Хүүхэд: {childLabel(item.child)}
                </span>
                {items.length > 1 && <span className="text-xs text-[#6e5e8a]">{items.indexOf(item) + 1} / {items.length} хүүхэд</span>}
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              {q.section && <span className="text-[11px] md:text-xs font-bold tracking-[0.12em] uppercase text-[#0e7490]">{q.section}</span>}
              <h2 ref={qRef} tabIndex={-1} className="m-0 text-[21px] md:text-[22px] font-bold leading-[1.35] tracking-[-0.01em] text-[#1c1c1c] min-h-[86px] outline-none">{q.text}</h2>
            </div>

            <div className="flex justify-between gap-1" role="group" aria-label="Үнэлгээ">
              {[1, 2, 3, 4, 5].map((n) => {
                const on = !a?.dontKnow && (a?.stars || 0) >= n;
                return (
                  <button key={n} onClick={() => pickStars(n)} aria-label={`${n} од`} aria-pressed={!a?.dontKnow && a?.stars === n}
                    className="h-[60px] w-[60px] rounded-[14px] flex items-center justify-center hover:bg-[#faf7ff] focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
                    <svg aria-hidden="true" width="46" height="46" viewBox="0 0 24 24">
                      <path d={STAR} fill={on ? "#f2cd30" : "#ffffff"} stroke={on ? "#c9a300" : "#d9cdea"} strokeWidth="1.3" strokeLinejoin="round" />
                    </svg>
                  </button>
                );
              })}
            </div>

            {s.allowDontKnow && (
              <button onClick={toggleDk} aria-pressed={!!a?.dontKnow}
                className={`h-11 rounded-xl border-[1.5px] text-sm font-semibold transition-colors ${a?.dontKnow ? "border-primary bg-[#f3eeff] text-primary" : "border-[#e5d8f5] bg-white text-[#5b4e70] hover:bg-[#faf7ff]"}`}>
                Мэдэхгүй / үнэлж чадахгүй
              </button>
            )}

            {isAnswered(a) && (
              <label className="flex flex-col gap-1.5">
                <span className="text-[13px] font-semibold text-[#1c1c1c]">Тайлбар <span className="font-normal text-[#6e5e8a]">(заавал биш)</span></span>
                <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} maxLength={2000} placeholder="Нэмж хэлэх зүйл байвал бичнэ үү"
                  className="w-full rounded-xl border border-[#e5d8f5] bg-white p-3 text-[16px] leading-snug text-[#1c1c1c] outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 resize-none" />
              </label>
            )}

            <div className="flex-1" />
            <div className="flex gap-2.5">
              <button onClick={() => qi > 0 && go(qi - 1)} disabled={qi === 0} aria-label="Өмнөх асуулт"
                className="h-[52px] w-[52px] shrink-0 rounded-full border border-[#e5d8f5] bg-white text-[#1c1c1c] flex items-center justify-center hover:bg-[#faf7ff] disabled:opacity-40 disabled:cursor-not-allowed">
                <FiChevronLeft size={22} />
              </button>
              <button onClick={next} disabled={!isAnswered(a) || busy}
                className="flex-1 h-[52px] rounded-full bg-primary text-white text-base font-bold hover:bg-[#4b0d76] disabled:bg-[#e9e8f0] disabled:text-[#8a8295] disabled:cursor-not-allowed">
                {last ? (busy ? "Илгээж байна…" : "Илгээх") : "Дараах"}
              </button>
            </div>
          </>
        ) : phase === "next" && nextChild ? (
          // Нэг хүүхдийнх илгээгдсэн — дараагийн хүүхэд рүү
          <div className="flex-1 flex flex-col items-center justify-center gap-3.5 text-center py-5">
            <span className="h-[72px] w-[72px] rounded-full bg-[#dcfce7] text-[#15803d] flex items-center justify-center"><FiCheck size={34} strokeWidth={2.6} /></span>
            <h2 className="m-0 text-[22px] font-bold text-[#1c1c1c]">{doneChild?.child?.firstName ? `${doneChild.child.firstName} — илгээгдлээ` : "Илгээгдлээ"}</h2>
            <p className="m-0 text-[15px] leading-relaxed text-[#5b4e70] max-w-[300px]">
              Одоо дараагийн хүүхдийнхээ талаар ижил асуултуудад хариулна уу. Үлдсэн: {left.length}
            </p>
            <span className="inline-flex items-center h-8 px-3.5 rounded-full bg-[#f3eeff] border border-[#e5d8f5] text-primary text-sm font-semibold">
              Хүүхэд: {childLabel(nextChild.child)}
            </span>
            <div className="flex-1" />
            <button onClick={() => begin(nextChild, s, answers)} className="w-full h-[52px] rounded-full bg-primary text-white text-base font-bold hover:bg-[#4b0d76]">
              {nextChild.child?.firstName ? `${nextChild.child.firstName} — эхлэх` : "Үргэлжлүүлэх"}
            </button>
            <button onClick={later} className="min-h-[44px] text-sm font-semibold text-primary hover:text-[#3d1b6e]">Дараа бөглөх</button>
          </div>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center gap-3.5 text-center py-5">
            <span className="h-[72px] w-[72px] rounded-full bg-[#dcfce7] text-[#15803d] flex items-center justify-center"><FiCheck size={34} strokeWidth={2.6} /></span>
            <h2 className="m-0 text-[22px] font-bold text-[#1c1c1c]">Баярлалаа!</h2>
            <p className="m-0 text-[15px] leading-relaxed text-[#5b4e70] max-w-[300px]">
              Таны хариулт хадгалагдлаа. Санал бүрийг сургуулийн удирдлага уншиж, сайжруулалтад ашиглана.
            </p>
            <div className="flex-1" />
            <button onClick={onClose} className="w-full h-[52px] rounded-full bg-primary text-white text-base font-bold hover:bg-[#4b0d76]">Хаах</button>
          </div>
        )}
      </div>
    </div>
  );
}
